'use strict';

var BookingRequestHelpers = require('./BookingRequest');
var price = require('../price');
var _ = require('lodash');
var moment = require('moment');

/**
 * @param {Payment} payment
 * @return payment object from payment schedule
 */
exports.mostRecent = function (payment) {
  if (!payment || !_.get(payment, 'schedule.length')) {
    return null;
  }

  return _.last(payment.schedule);
};

/**
 * Push a completed job to a request's array of payments
 *
 * @param {Request} request
 * @param {FullTransactionResponse} transaction, braintree transaction response
 * @param {Options} options, such as title and description
 * @return {CompletedJob} completedJob
 */
exports.getProcessedJob = function (request, fullTransactionResponse, options) {
  var state = 'COMPLETED';
  var transaction = void 0;

  // Braintree sends back different structured responses based on success or error. If you pay with
  // a card and it fails, the braintree response still has a transaction object you can use.
  // Otherwise, the only info available is on the `params` object.

  if (_.get(fullTransactionResponse, 'success') || _.get(fullTransactionResponse, 'transaction')) {
    transaction = _.get(fullTransactionResponse, 'transaction');
  } else {
    transaction = _.get(fullTransactionResponse, 'params.transaction');
  }

  var amountDollarsStr = _.get(transaction, 'amount') || _.get(options, 'amount') || '0';
  var amountCents = _.round(parseFloat(amountDollarsStr) * 100);

  var date = moment().format('YYYY-MM-DD');

  var completedJob = {
    state: state,
    amountCents: amountCents,
    date: date,
    time: Date.now()
  };

  if (!options || !options.title) {
    var numSuccessfulPayments = _.filter(BookingRequestHelpers.paymentSchedule(request), function (p) {
      return p.state === 'COMPLETED';
    }).length;

    if (!numSuccessfulPayments) {
      completedJob.title = 'DEPOSIT';
    }
  }

  // store space
  var space = BookingRequestHelpers.selectedSpace(request);
  if (space) {
    completedJob.space = _.get(space, 'data.name');
  }

  var menu = _.get(request, 'data.menu');
  if (menu) {
    completedJob.menu = menu.none ? {
      none: true
    } : {
      name: menu.name,
      numGuests: menu.numGuests,
      pricePerGuestCents: menu.pricePerGuestCents
    };
  }

  var drinks = _.get(request, 'data.drinks', []);
  completedJob.drinks = transformDrinks(drinks);

  var addOns = _.get(request, 'data.addOns', []);
  completedJob.addOns = transformAddOns(addOns);

  if (transaction) {
    completedJob.token = _.get(transaction, 'creditCard.token');
    completedJob.cardDetails = getCardDetails(transaction);
    completedJob.response = fullTransactionResponse;
  }

  if (options) {
    _.merge(completedJob, options);
  }

  return completedJob;
};

exports.getProcessedRefundJob = function (request, fullTransactionResponse, options) {
  var state = 'REFUNDED';
  var title = 'REFUND';
  var transaction = void 0;

  if (_.get(fullTransactionResponse, 'success') || _.get(fullTransactionResponse, 'transaction')) {
    transaction = _.get(fullTransactionResponse, 'transaction');
  } else {
    transaction = _.get(fullTransactionResponse, 'params.transaction');
  }

  var amountDollarsStr = _.get(transaction, 'amount') || '0';
  var amountCents = _.round(parseFloat(amountDollarsStr) * 100);

  var date = moment().format('YYYY-MM-DD');

  var completedJob = {
    state: state,
    title: title,
    amountCents: amountCents,
    date: date,
    time: Date.now()
  };

  if (transaction) {
    completedJob.token = _.get(transaction, 'creditCard.token');
    completedJob.cardDetails = getCardDetails(transaction);
    completedJob.response = fullTransactionResponse;
  }

  if (options) {
    _.merge(completedJob, options);
  }

  return completedJob;
};

/**
 * Get the first successful payment in the payment schedule (the Deposit payment)
 */
exports.firstCompletedPayment = function (payment) {
  return _.find(payment.schedule, function (p) {
    return p.state === 'COMPLETED';
  });
};

/**
 * create a completed payment object
 * @param {Request} request
 * @param {FullTransactionResponse} fullTransactionResponse, braintree transaction response
 * @param {Options} options
 * @mutates payment
 */
exports.initCompletedPayment = function (request, fullTransactionResponse, options) {
  var completedJob = exports.getProcessedJob(request, fullTransactionResponse, options);

  var payment = request.data.payment;
  if (_.get(payment, 'schedule')) {
    payment.schedule.push(completedJob);
  } else {
    _.set(payment, 'schedule', [completedJob]);
  }

  var breakdown = price.fullPriceBreakdownCents(request);
  _.last(request.data.payment.schedule).costBreakdownCents = breakdown;
};

exports.addCompletedRefund = function (request, fullTransactionResponse, options) {
  var completedRefund = exports.getProcessedRefundJob(request, fullTransactionResponse, options);

  var payment = request.data.payment;
  var schedule = _.get(request, 'data.payment.schedule', []);
  var completedPayments = schedule.filter(function (payment) {
    return payment.state === 'COMPLETED';
  });

  if (_.get(payment, 'schedule') && completedPayments.length) {
    payment.schedule.push(completedRefund);
  }
  var breakdown = price.fullPriceBreakdownCents(request);
  _.last(request.data.payment.schedule).costBreakdownCents = breakdown;
};

/**
 * Create a failed payment object
 *
 * @param {Request} request
 * @param {FullTransactionResponse} fullTransactionResponse, braintree transaction response
 * @param {Options} options
 * @mutates payment
 */
exports.initFailedPayment = function (request, fullTransactionResponse, options) {
  var erroredState = 'ERRORED';
  var payment = _.get(request, 'data.payment');
  options = options || {};

  _.merge(options, { state: erroredState });

  var failedJob = exports.getProcessedJob(request, fullTransactionResponse, options);
  if (_.get(payment, 'schedule')) {
    payment.schedule.push(failedJob);
  } else {
    _.set(payment, 'schedule', [failedJob]);
  }
};

/**
 * 
 * @param {Request} request 
 * @param {FullTransactionResponse} fullTransactionResponse, braintree transaction response
 * @mutates payment
 */

exports.handleFailedRefund = function (request, fullTransactionResponse, options) {
  var state = 'ERRORED';
  var payment = _.get(request, 'data.payment');
  options = options || {};

  _.merge(options, { state: state });

  var failedJob = exports.getProcessedRefundJob(request, fullTransactionResponse, options);
  payment.schedule.push(failedJob);
};

/**
 * Display price for a specific overage in a payment's array of overages
 *
 * @param {Overage} overage
 * @return {Number}
 */
exports.overagePriceCents = function (overage) {
  if (!overage) {
    return 0;
  }

  var subtotal = overage.amountCents * overage.quantity;
  return _.isFinite(subtotal) ? _.round(subtotal, 2) : 0;
};

/**
 * Gets an array concatting all the attachments array of all the overages
 *
 * @param {Array} overages
 * @return {Array}
 */
exports.overageAttachments = function (overages) {
  return _.flatten(_.map(overages, function (overage) {
    return overage.attachments;
  }));
};

/**
 * Gets an array concatting all the attachments arrays of all the payments
 *
 * @param {Array} schedule
 * @return {Array}
 */
exports.paymentAttachments = function (schedule) {
  return _.flatten(_.map(overages, function (payment) {
    return payment.attachments;
  }));
};

/**
 * transform request drinks into drink data for payment schedule obj
 * @param {Array} drinks
 * @return {Array}
 */
function transformDrinks(drinks) {
  if (!_.get(drinks, 'length')) {
    return [];
  }
  return drinks.filter(function (drink) {
    return !drink.isDeleted;
  }).map(function (drink) {
    var name = drink.name,
        payAtTheVenue = drink.payAtTheVenue,
        selectedDurationMinutes = drink.selectedDurationMinutes,
        numGuests = drink.numGuests,
        costPerGuestCents = drink.costPerGuestCents;

    return {
      name: name,
      payAtTheVenue: payAtTheVenue,
      selectedDurationMinutes: selectedDurationMinutes,
      numGuests: numGuests,
      costPerGuestCents: costPerGuestCents
    };
  });
}

/**
 * @param {Array} addOns
 * @return {Array}
 */
function transformAddOns(addOns) {
  if (!_.get(addOns, 'length')) {
    return [];
  }

  return addOns.filter(function (addOn) {
    return !addOn.isDeleted;
  }).map(function (addOn) {
    var item = addOn.item,
        priceInCents = addOn.priceInCents,
        numGuests = addOn.numGuests,
        priceType = addOn.priceType,
        taxType = addOn.taxType;

    return {
      item: item,
      priceInCents: priceInCents,
      numGuests: numGuests,
      priceType: priceType,
      taxType: taxType
    };
  });
}

/**
 * get card details for each payment in schedule - last4, card type, exp date, ch name
 * @param {Transaction} transaction
 */
function getCardDetails(transaction) {
  var theCard = _.get(transaction, 'creditCard');
  if (!theCard) {
    return;
  }

  return {
    last4: theCard.last4,
    cardType: theCard.cardType,
    expirationMonth: theCard.expirationMonth,
    expirationYear: theCard.expirationYear,
    cardholderName: theCard.cardholderName
  };
}

exports.determineRecipient = function (payment) {
  var schedule = _.get(payment, 'schedule', []);
  var completedPayments = schedule.filter(function (payment) {
    return payment.state === 'COMPLETED';
  });

  if (!completedPayments.length) {
    return '';
  }

  if (completedPayments.every(function (completedPayment) {
    return !completedPayment.isDirectToVenue;
  })) {
    return 'SixPlus';
  }

  if (completedPayments.every(function (completedpayment) {
    return completedpayment.isDirectToVenue;
  })) {
    return 'Venue';
  }

  return 'Mixed';
};

exports.determinePaymentMethod = function (payment) {
  var schedule = _.get(payment, 'schedule', []);
  var completedPayments = schedule.filter(function (payment) {
    return payment.state === 'COMPLETED';
  });

  if (!completedPayments.length) {
    return '';
  }

  if (completedPayments.every(function (completedPayment) {
    return !completedPayment.isManual;
  })) {
    return 'Website Booking';
  }

  var uniquePaymentMethods = _.uniqBy(completedPayments, 'method');
  if (uniquePaymentMethods.length > 1) {
    return 'Mixed';
  }
  return uniquePaymentMethods[0].method;
};